/* eslint-disable camelcase */
import { store } from "~/state/store";
import { node, icon } from "~/util/dom";
import { CLAMP_LEN_CHARS, CLAMP_LEN_LINES, UNSET_SMILEY_VAL, REVIEW_THANK_YOU_TO } from "~/review/const";
import { IReview, IReviewItem, TStructuredData, IReviewOpenData } from "~/review/interfaces";
import { IReviewConf, IPoi, IEventPoiagg } from "~/interfaces";
import { getTitle } from "~/util/detail";
import { _ } from "~/i18n";
import { IStructuredItem } from "@mapy/review-components";

interface IClampData {
	original: string;
	clamped: string;
}

export function isTwin(poiData: IPoi | IEventPoiagg): boolean {
	return poiData?.source === "base" && (poiData?.review?.type === "firm" || poiData?.poilink?.value?.filter(item => item.review).length > 0);
}

export function isFirmFromPoi(poiData: IPoi | IEventPoiagg): boolean {
	return poiData?.source === "firm" || isTwin(poiData);
}

export function isFirmFromReview(reviewData: IReviewItem): boolean {
	return reviewData?.type === "firm";
}


export function reviewHasBadge(review: IReview) {
	return review.badge !== "none";
}

export function reviewHasColoredBadge(review: IReview) {
	return review.badge === "gray" || review.badge === "green";
}

export function reviewHasGreenBadge(review: IReview) {
	return review.badge === "green";
}

export function reviewHasCountBadge(review: IReview) {
	return review.badge === "count";
}

export function reviewGetBadgeColor(review: IReview): IReview["badge"] {
	return review.badge;
}

export function getBadgeComponent(): "mapy-review-badge-new" {
	return "mapy-review-badge-new";
}

export function getBadgeComposedComponent(): "mapy-review-badge-composed-new" {
	return "mapy-review-badge-composed-new";
}

/**
 * zaloguje firemní data
 * @param  {String} action název akce
 * @param  {IPoi} data data specifická pro firmy
 */
export function firmStats(action = "", data: Pick<IPoi, "stats" | "id" | "inactive">, specParam: {[key: string]: string | boolean | number | object} = {}) {
	if (!data.stats) {
		return;
	}

	const query = Mapy.getComponent("search").getQuery();

	Mapy.getComponent("logger").log(action, {
		premiseId: data.id, // (id pobočky)
		subjectId: data.stats.subjectId, // (id celé firmy)
		isPaid: data.stats.isPaid,
		isDisabled: !!data.inactive,
		...query && query !== ""
			? {
				categories: data.stats.categories, // - společně s výsledkem firmy pošlou rozklad dotazu na firemní kategorie, tento rozklad se bude odesílat do statistik. Rozklad slouží k vyhodnocení výkonu jednotlivých kategorií pro firmu.
				query,
			}
			: {},
		...specParam,
	});
}

/**
 a) scénáře ještě jsem nehodnotil myReview null
 b) scénáře už jsem hodnotil myReview true

 1) nemame zadne hodnoceni
 2) nemame zadne hodnoceni, mame booking
 3) mame malo hodnoceni
 4) mame malo hodnoceni, mame booking
 5) mame hodnoceni
 6) mame hodnoceni, mame zjistit cenu a dostupnost do vlastního rezervačního systému
 7) mame hodnoceni, mame booking
 */
export function getFirmLayout(firmReview: IReview, bookingData: { url: string }, myReview: IReviewItem, isFirmReview: boolean): string {
	if (!isFirmReview) {
		return "0";
	}

	let layout = "";

	if (firmReview || bookingData) {
		const isBooking = bookingData && bookingData.url.indexOf("booking.com") !== -1;

		layout = myReview ? myReview.actual === false ? "c" : "b" : "a";

		if (firmReview && !isBooking && !firmReview.total) {
			layout += "1";
		} else if ((!firmReview && isBooking) || (!firmReview.total && isBooking)) {
			layout += "2";
		} else if (firmReview && !isBooking && firmReview.review_weight < 3) {
			layout += "3";
		} else if (firmReview && isBooking && firmReview.review_weight < 3) {
			layout += "4";
		} else if (firmReview && !isBooking && firmReview.review_weight >= 3) {
			layout += "5";
		} else if (firmReview && isBooking && firmReview.review_weight >= 3) {
			layout += "6";
		} else {
			layout += "7";
		}
	}

	return layout;
}

export function getReportReasonsList(isFirm: boolean, hasPhotos: boolean): Array<{ id: number; transText: string; key?: string; fillText?: boolean; }> {
	if (isFirm) {
		return [{
			id: 1,
			transText: "detail.content.review2.report.reason1",
		},
		{
			id: 2,
			transText: "detail.content.review2.report.reason2",
		},
		{
			id: 3,
			transText: "detail.content.review2.report.reason3",
		},
		{
			id: 4,
			transText: "detail.content.review2.report.reason5",
		},
		...hasPhotos
			? [{
				id: 6,
				transText: "detail.content.review2.report.reasonBadPhoto",
			},
			{
				id: 7,
				transText: "detail.content.review2.report.reasonPhotoContain",
			}]
			: [],
		{
			id: 5,
			fillText: true,
			transText: "detail.content.review2.report.reason4",
		}];
	}

	return [{
		id: 1,
		key: "content",
		transText: "detail.content.review2.report.reasonContent",
	},
	{
		id: 2,
		key: "privacy",
		transText: "detail.content.review2.report.reasonPrivacy",
	},
	{
		id: 3,
		key: "irrelevant",
		transText: "detail.content.review2.report.reasonIrrelevant",
	},
	{
		id: 4,
		key: "legal",
		transText: "detail.content.review2.report.reasonLegal",
	},
	{
		id: 5,
		key: "advert",
		transText: "detail.content.review2.report.reasonAdvert",
	},
	...hasPhotos
		? [{
			id: 7,
			key: "photo_quality",
			transText: "detail.content.review2.report.reasonBadPhoto",
		}, {
			id: 8,
			key: "photo_content",
			transText: "detail.content.review2.report.reasonPhotoContain",
		}]
		: [],
	{
		id: 6,
		fillText: true,
		key: "custom",
		transText: "detail.content.review2.report.reasonCustom",
	}];
}

function clampClick(elem: HTMLElement, clampData: IClampData, noHide: boolean, onHideClick: (event: MouseEvent) => void) {
	const clickCb = (event: MouseEvent) => {
		if ((event.target as HTMLElement).classList.contains("show-full-text")) {
			elem.innerHTML = clampData.original;

			if (typeof onHideClick === "function") {
				onHideClick(event);
			}

			if (noHide) {
				elem.removeEventListener("click", clickCb);
			} else {
				const hideT = node("a", { href: "#", className: "hide-full-text" }, _("photo.hidetext"));

				hideT.style.display = "block";
				elem.appendChild(hideT);
			}
		} else if ((event.target as HTMLElement).classList.contains("hide-full-text")) {
			elem.innerHTML = clampData.clamped;
		}
	};

	elem.addEventListener("click", clickCb);
}

// el musi mit nastaveny innerHTML
// vraci true - byl clampled, jinak false
export function clamp(elem: HTMLElement, noHide?: boolean, onHideClick?: () => void): boolean {
	// neexistuje
	if (typeof window.$clamp !== "function") {
		return false;
	}

	const text = elem.innerHTML;
	const linesLen = text.split("\n").length;

	// ma cenu clamp?
	if ((linesLen === 1 && text.length > CLAMP_LEN_CHARS) || (linesLen > 1)) {
		const clampOutput = window.$clamp(elem, {
			clamp: CLAMP_LEN_LINES + 1, // 10 radku
			truncationHTML: `<a href="#" class="show-full-text">${_("photo.showtext")}</a>`,
			useNativeClamp: false,
		}) as IClampData;

		elem.innerHTML = clampOutput.clamped || clampOutput.original;

		if (clampOutput.clamped) {
			clampClick(elem, clampOutput, noHide, onHideClick);

			return true;
		}
	}

	return false;
}

export function getFirmImpressConfig(conf) {
	const state = store.getState();
	const { user } = state.user;
	const { query } = state.search;
	const { data: detailData } = state.detail;
	const data = {
		reviewId: conf.reviewId || 0,
		service: "mapy",
		widget: false,
		reviewStatus: conf.state || "",
		reviewOrder: conf.reviewOrder || 1,
		reviewShortened: conf.reviewShortened || false,
		userLogged: user.state === "green" || user.state === "orange",
		reviewAuthor: conf.reviewAuthor || false,
		reviewShowMoreAction: conf.reviewShowMoreAction || false,
		refused: conf?.state === "rejected" ? 1 : 0,
		query,
		...conf,
	};

	if (detailData && detailData.stats) {
		data.premiseId = detailData.id;
		data.subjectId = detailData.stats.subjectId;
		data.isPaid = detailData.stats.isPaid;

		if (query && detailData.stats.categories) {
			data.categories = detailData.stats.categories;
		}
	}

	return {
		action: "reviewImpress",
		data,
	};
}

export function getStarsRating(review: IReview, starsCount = 5): string {
	let starsPercent = 0;

	if (typeof review.review_rating_stars === "number" && reviewHasColoredBadge(review)) {
		// cislo 4 -> 4,0 bacha na tecku!
		starsPercent = review.review_rating_stars;
	} else {
		starsPercent = Math.max(review.review_rating, 0) / 100 * starsCount;
	}

	return Mapy.Util.formatNumber(starsPercent, 1, false, true);
}

export function createTimeHint(lang: string, time: number, long = false): string {
	const dateInput = new Date(time * 1000);
	const dateNow = new Date();
	const dateWeekdaysStart = new Date();

	dateWeekdaysStart.setHours(0);
	dateWeekdaysStart.setMinutes(0);
	dateWeekdaysStart.setSeconds(0);

	const dateWeekdaysEnd = new Date(dateWeekdaysStart);

	dateWeekdaysStart.setDate(dateNow.getDate() - 1);
	dateWeekdaysEnd.setDate(dateNow.getDate() - 6);

	const dateYesterday = new Date();

	dateYesterday.setDate(dateYesterday.getDate() - 1);

	switch (true) {
		// dnes
		case dateNow.getFullYear() === dateInput.getFullYear() && dateNow.getMonth() === dateInput.getMonth() && dateNow.getDate() === dateInput.getDate():
			return long ? _("activerequest.visitedToday") : _("today");
		// vcera
		case dateYesterday.getFullYear() === dateInput.getFullYear() && dateYesterday.getMonth() === dateInput.getMonth() && dateYesterday.getDate() === dateInput.getDate():
			return long ? _("activerequest.visitedYesterday") : _("yesterday");
		// poslednich 7 dnu bez dneska a vcerejska
		case dateInput.getTime() < dateWeekdaysStart.getTime() && dateInput.getTime() > dateWeekdaysEnd.getTime():
			return long ? _("activerequest.visitedLastWeek", { daysAgo: Math.floor((dateWeekdaysStart.getTime() - dateInput.getTime()) / 24 / 60 / 60 / 1000) + 2 }) : new Intl.DateTimeFormat(lang, { weekday: "long" }).format(dateInput);
		// jiny rok
		case dateNow.getFullYear() !== dateInput.getFullYear():
			return long ? _("activerequest.visitedYear", { year: dateInput.getFullYear() }) : new Intl.DateTimeFormat(lang, { year: "numeric" }).format(dateInput);
		// jiny mesic
		case dateNow.getMonth() !== dateInput.getMonth():
			return long ? _(`activerequest.visitedMonth.${dateInput.getMonth()}`) : new Intl.DateTimeFormat(lang, { year: "numeric", month: "long" }).format(dateInput);
		// stejny mesic i rok, ale ani dnes ani vcera
		default:
			return long ? _("activerequest.visitedThisMonth") : new Intl.DateTimeFormat(lang, { year: "numeric", month: "long", day: "numeric" }).format(dateInput);
	}
}

export function getReviewConf(data: any, likeData: any = null, reviewMode = "", drrid = 0, immediatelyOpen = false, openData?: IReviewOpenData): IReviewConf {
	return {
		source: data.source,
		id: data.id,
		typeId: data.typeId || -1,
		typeName: data.typeName,
		title: getTitle(data, likeData),
		desc: data.description,
		myReview: data.myReview,
		rusAdmin: data.rus_admin,
		review: data.review,
		stats: data.stats,
		booking: data.extend?.poi?.booking,
		recenze: data.recenze,
		inactive: data.inactive,
		zoomToReviews: data.zoomToReviews,
		manageMyCompany: data.admin_urls?.edit,
		structuredRating: data.review?.structured_rating || [],
		reviewMode,
		drrid,
		isFirm: isFirmFromPoi(data),
		isTwin: isTwin(data),
		...data.openReviewPopup ? { openReviewPopup: data.openReviewPopup } : {},
		immediatelyOpen,
		openData,
		mark: {
			lat: data.mark.lat,
			lon: data.mark.lon,
		},
	};
}

export function getUserName() {
	const { user } = store.getState().user;

	return user.name || user.accountDisplayName.replace(/[@].*/u, "");
}

export function getStructuredRating(myReview: IReviewItem): TStructuredData {
	const structuredRating = myReview.structured_rating || [];

	return structuredRating.map(item => ({
		key: item.key,
		value: item.rating,
	}));
}

export function parseStructuredRating(structuredRating: TStructuredData, full?: boolean): {
	attributesCount: number;
	attributesSetCount: number;
	attributesSet?: string;
	attributesUnset?: string;
} {
	const filled = [];
	const unset = [];

	Object.entries(structuredRating).forEach(entry => {
		if (entry[1].value === UNSET_SMILEY_VAL) {
			unset.push(entry[1].key);
		} else {
			filled.push(entry[1].key);
		}
	});

	return {
		attributesCount: filled.length + unset.length,
		attributesSetCount: filled.length,
		...full
			? {
				attributesSet: filled.join(","),
				attributesUnset: unset.join(","),
			}
			: {},
	};
}

export function createDeleteInfo() {
	const hearthEl = node("div", { className: "firm-review-popup-hearth" });

	hearthEl.appendChild(icon("delete"));
	hearthEl.appendChild(node("h3", { textContent: _("detail.content.review2.remove.afterDelete") }));
	document.body.appendChild(hearthEl);

	setTimeout(() => {
		hearthEl.remove();
	}, REVIEW_THANK_YOU_TO);
}

export function hasFilledStructuredReviews(items: Array<IStructuredItem>) {
	if (items && Array.isArray(items)) {
		const filtered = items.filter(item => item.rating !== null);

		return filtered.length > 0 ? 1 : 0;
	}

	return 0;
}
